import { OrderBusinessCardType } from '@app/store/reducers/salaryCards/businessCardOrder.api'
import {
  CardCurrenyType,
  CardType,
  OrderType,
  OrderDeliveryType,
  PasswordType,
} from '../constants/fieldValues.constant'
import moment from 'moment'
import * as yup from 'yup'

export const BusinessCardFormSchema: yup.ObjectSchema<
  Omit<OrderBusinessCardType, 'commission'>
> = yup.object().shape({
  cardName: yup
    .string()
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  cardId: yup
    .string()
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  cardAccount: yup
    .string()
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  transferredBranch: yup
    .string()
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  frontFileId: yup.string().when('cardHolder.isDocExpired', {
    is: (isDocExpired: any) => !isDocExpired, // isDocExpired varsa
    then: (schema) => schema.required('orders.salaryCardOrder.restrictionTexts.requiredFields'), // required yap
    otherwise: (schema) => schema.nullable().notRequired(), // optional yap
  }),
  backFileId: yup.string().optional(),
  cardCurrency: yup
    .mixed<CardCurrenyType>()
    .oneOf(
      Object.values(CardCurrenyType),
      'orders.salaryCardOrder.restrictionTexts.selectedType'
    )
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  cardDuration: yup
    .string()
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  cardSystem: yup
    .mixed<CardType>()
    .oneOf(
      Object.values(CardType),
      'orders.salaryCardOrder.restrictionTexts.selectedType'
    )
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  orderType: yup
    .mixed<OrderType>()
    .oneOf(
      Object.values(OrderType),
      'orders.salaryCardOrder.restrictionTexts.selectedType'
    )
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  orderDeliveryType: yup
    .mixed<OrderDeliveryType>()
    .oneOf(
      Object.values(OrderDeliveryType),
      'orders.salaryCardOrder.restrictionTexts.selectedType'
    )
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  accountBranch: yup
    .string()
    .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  note: yup
    .string()
    .max(50, 'orders.salaryCardOrder.restrictionTexts.checkNote')
    .nullable(),
  trustedPerson: yup.object().shape({
    id: yup.number().nullable(),
    expiryDate: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .test(
        'is-valid-date-range',
        'orders.salaryCardOrder.restrictionTexts.invalidDateRange',
        function (value) {
          if (!value) return false

          const enteredDate = moment(value, 'DD-MM-YYYY')
          if (!enteredDate.isValid()) return false

          const now = moment()
          const minDate = now.clone().add(1, 'month')
          const maxDate = now.clone().add(5, 'years')

          return enteredDate.isBetween(minDate, maxDate, undefined, '[]')
        }
      ),
    fullName: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(1, 'orders.salaryCardOrder.restrictionTexts.requiredFields')
      .matches(
        /^[a-vx-zA-VX-ZçöüğşiıəÇÖİÜĞŞIƏwW.\s]+$/,
        'orders.salaryCardOrder.restrictionTexts.checkfullName'
      ),
    pin: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(3, 'orders.salaryCardOrder.restrictionTexts.minLengthPin')
      .matches(
        /^[A-Za-z0-9]+$/,
        'orders.salaryCardOrder.restrictionTexts.pinCheck'
      ),
    phone: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(1, 'orders.salaryCardOrder.restrictionTexts.requiredFields')
      .matches(
        /^\((50|51|55|70|77|90|99|10)\) \d{3}-\d{2}-\d{2}$/,
        'orders.salaryCardOrder.restrictionTexts.mobilePhoneCheck'
      ),
  }),
  cardHolder: yup.object().shape({
    isMe: yup.boolean().optional(),
    cardHolderFullName: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(1, 'orders.salaryCardOrder.restrictionTexts.requiredFields')
      .matches(
        /^[a-vx-zA-VX-ZçöüğşiıəÇÖİÜĞŞIƏ.\s]+$/,
        'orders.salaryCardOrder.restrictionTexts.checkfullName'
      ),
    pin: yup
      .string()
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/,
        'orders.salaryCardOrder.restrictionTexts.alphanumeric'
      )
      .length(7, 'orders.salaryCardOrder.restrictionTexts.pinLength'),
    phoneNumber: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(1, 'orders.salaryCardOrder.restrictionTexts.requiredFields')
      .matches(
        /^\((50|51|55|70|77|90|99|10)\) \d{3}-\d{2}-\d{2}$/,
        'orders.salaryCardOrder.restrictionTexts.mobilePhoneCheck'
      ),
    serialType: yup
      .string()
      .oneOf(
        Object.values(PasswordType),
        'orders.salaryCardOrder.restrictionTexts.serialType'
      )
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
    serialNo: yup
      .mixed()
      .test('is-valid-serial', function (value) {
        const { serialType } = this.parent
        const checkLength =
          [PasswordType.AA, PasswordType.AB].includes(serialType)
            ? `${value}`.length === 7
            : `${value}`.length === 8
        return (
          checkLength ||
          this.createError({
            path: this.path,
            message:
              [PasswordType.AA, PasswordType.AB].includes(serialType)
                ? 'orders.salaryCardOrder.restrictionTexts.isValidSerial7'
                : 'orders.salaryCardOrder.restrictionTexts.isValidSerial8',
          })
        )
      })
      .test(
        'is-valid-number',
        'orders.salaryCardOrder.restrictionTexts.isValidNumber',
        function (val) {
          const check = !isNaN(Number(val)) && val != 0
          return check
        }
      )
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
    mobileOperatorName: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
  }),
})
